import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Percent,
  ShieldCheck,
  DollarSign,
  Users,
  Calendar,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
  Edit3,
} from "lucide-react";

import { getMonthlyObjectives } from "@/lib/pilotageApi";
import { getApiErrorMessage } from "@/lib/api";
import { MONTH_NAMES, MONTHLY_FIELDS, formatMonthlyValue } from "@/lib/monthlyObjectives";
import { formatPilotageDecimal } from "@/lib/pilotageUi";

import MonthlyTimelineNav from "./MonthlyTimelineNav";
import MonthlyAnnualSynthesis from "./MonthlyAnnualSynthesis";
import MonthlyExecutiveCharts from "./MonthlyExecutiveCharts";

const STATUS_LABELS = {
  OK: "Objectif Atteint",
  WARN: "Presque Atteint",
  BAD: "Sous l'Objectif",
  "N/A": "Non Comparable",
};

export default function MonthlyObjectivesDashboard({
  year,
  objectiveId,
  isRh = false,
  cockpitData = null,
}) {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    const requested = Number(new URLSearchParams(window.location.search).get("goal_month"));
    if (Number.isInteger(requested) && requested >= 1 && requested <= 12) return requested;
    return year === now.getFullYear() ? Math.max(1, now.getMonth()) : year < now.getFullYear() ? 12 : 1;
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (isRh) return undefined;
    let ignore = false;
    setLoading(true);
    setData(null);
    setError("");

    getMonthlyObjectives(year, month, objectiveId)
      .then((payload) => {
        if (!ignore) setData(payload);
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(getApiErrorMessage(requestError, "Impossible de charger les objectifs mensuels."));
        }
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [year, month, objectiveId, isRh, refresh]);

  if (isRh) {
    return (
      <p className="rounded-xl border border-white/10 p-5 text-slate-300">
        Les objectifs mensuels sont réservés à l’administration.
      </p>
    );
  }

  const selectMonth = (value) => {
    setMonth(value);
    const params = new URLSearchParams(window.location.search);
    params.set("goal_month", value);
    window.history.replaceState(null, "", `${window.location.pathname}?${params}${window.location.hash}`);
  };

  const closed = data?.state === "CLOSED";
  const editParams = new URLSearchParams({
    year: String(year),
    view: "monthly",
    month: String(month).padStart(2, "0"),
  });
  if (data?.objective?.id || objectiveId) {
    editParams.set("objective_id", data?.objective?.id || objectiveId);
  }

  // Field helper to retrieve metrics
  const getFieldData = (key) => {
    const target = data?.targets?.[key];
    const actual = data?.realized?.[key];
    const forecast = data?.forecast?.[key];
    const delta = data?.delta?.[key];
    const status = data?.status?.[key] || "N/A";
    const fieldDef = MONTHLY_FIELDS.find((f) => f.key === key) || { key, label: key };
    return { target, actual, forecast, delta, status, fieldDef };
  };

  const caMetric = getFieldData("annual_revenue_target");
  const marginMetric = getFieldData("margin_target");
  const etpMetric = getFieldData("total_annual_billed_etp");
  const tjmMetric = getFieldData("tjm_target");
  const fixedCostsMetric = getFieldData("fixed_costs_target");
  const netResultMetric = getFieldData("forecast_result_target");
  const totalHeadcountMetric = getFieldData("total_resources_end_year");
  const entriesMetric = getFieldData("entries_per_month");

  return (
    <section
      className="min-w-0 space-y-6 rounded-2xl border border-white/10 bg-slate-900/50 p-4 sm:p-6"
      data-testid="monthly-objectives-dashboard"
    >
      {/* SECTION A — EN-TÊTE DU COCKPIT */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
              Performance &amp; Objectifs
            </h2>
            <span className="rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2.5 py-0.5 text-xs font-semibold text-lenvia-blue">
              Exercice {year}
            </span>
            {data?.objective?.name && (
              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs font-medium text-slate-300">
                {data.objective.name}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-400">
            Pilotage de la performance commerciale, financière et opérationnelle de LENVIA
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-slate-950/60 px-2.5 py-1 text-slate-300 font-medium">
              <Calendar className="h-3.5 w-3.5 text-lenvia-blue" />
              <span>Mois actif : {MONTH_NAMES[month - 1]} {year}</span>
            </span>

            {data && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 font-medium ${
                  closed
                    ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300"
                    : data.state === "CURRENT"
                    ? "border-lenvia-blue/30 bg-lenvia-blue/10 text-blue-200"
                    : "border-slate-700/50 bg-slate-800/60 text-slate-300"
                }`}
              >
                {closed ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <Clock className="h-3.5 w-3.5 text-lenvia-blue" />
                )}
                <span>
                  {closed
                    ? "Mois clôturé · comparaison au réalisé"
                    : data.state === "CURRENT"
                    ? "Mois en cours · pilotage à date"
                    : "Mois à venir · objectifs à atteindre"}
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to={`/admin/pilotage/objectifs?${editParams}`}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-lenvia-blue/40 bg-lenvia-blue/10 px-4 text-sm font-semibold text-lenvia-blue shadow-sm transition hover:bg-lenvia-blue/20"
          >
            <Edit3 className="h-4 w-4" />
            <span>Renseigner les mois</span>
          </Link>
        </div>
      </div>

      {/* SECTION B — VUE ANNUELLE MACRO */}
      <MonthlyAnnualSynthesis
        year={year}
        objective={data?.objective}
        cockpitData={cockpitData}
      />

      {/* SECTION C — FRISE TEMPORELLE 12 MOIS */}
      <MonthlyTimelineNav
        year={year}
        selectedMonth={month}
        onSelectMonth={selectMonth}
        monthsData={data?.months || []}
        currentState={data?.state}
      />

      {/* ÉTATS CHARGEMENT ET ERREURS */}
      {loading && (
        <div className="flex items-center justify-center py-12" role="status">
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-950/60 px-5 py-3 text-slate-400">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-lenvia-blue border-t-transparent" />
            <p className="text-sm">Chargement du mois…</p>
          </div>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-4 text-sm text-rose-300"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 flex-none text-rose-400" />
            <div>
              <p className="font-semibold">{error}</p>
              <button
                type="button"
                onClick={() => setRefresh((v) => v + 1)}
                className="mt-2 min-h-11 rounded-lg border border-rose-500/30 px-3 text-xs font-medium text-white transition hover:bg-rose-500/20"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DONNÉES DU MOIS SÉLECTIONNÉ */}
      {data && (
        <div className="space-y-6">
          {/* SECTION D — LES 4 KPI PRIORITAIRES DE DIRECTION */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-lenvia-blue" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Les 4 KPI Prioritaires — {MONTH_NAMES[month - 1]} {year}
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* KPI 1 : Chiffre d'Affaires Net */}
              <ExecutiveKpiCard
                fieldDef={caMetric.fieldDef}
                target={caMetric.target}
                actual={caMetric.actual}
                forecast={caMetric.forecast}
                delta={caMetric.delta}
                status={caMetric.status}
                closed={closed}
                accentColor="border-blue-500/30"
                icon={<TrendingUp className="h-5 w-5 text-lenvia-blue" />}
                questionLabel="Sommes-nous en avance ou en retard sur le CA ?"
              />

              {/* KPI 2 : Marge */}
              <ExecutiveKpiCard
                fieldDef={marginMetric.fieldDef}
                target={marginMetric.target}
                actual={marginMetric.actual}
                forecast={marginMetric.forecast}
                delta={marginMetric.delta}
                status={marginMetric.status}
                closed={closed}
                accentColor="border-emerald-500/30"
                icon={<Percent className="h-5 w-5 text-emerald-400" />}
                questionLabel="Notre rentabilité est-elle satisfaisante ?"
                isMargin
              />

              {/* KPI 3 : ETP Facturés */}
              <ExecutiveKpiCard
                fieldDef={etpMetric.fieldDef}
                target={etpMetric.target}
                actual={etpMetric.actual}
                forecast={etpMetric.forecast}
                delta={etpMetric.delta}
                status={etpMetric.status}
                closed={closed}
                accentColor="border-purple-500/30"
                icon={<ShieldCheck className="h-5 w-5 text-purple-400" />}
                questionLabel="La production évolue-t-elle comme prévu ?"
              />

              {/* KPI 4 : TJM Net Moyen */}
              <ExecutiveKpiCard
                fieldDef={tjmMetric.fieldDef}
                target={tjmMetric.target}
                actual={tjmMetric.actual}
                forecast={tjmMetric.forecast}
                delta={tjmMetric.delta}
                status={tjmMetric.status}
                closed={closed}
                accentColor="border-amber-500/30"
                icon={<DollarSign className="h-5 w-5 text-amber-400" />}
                questionLabel="Nos compétences sont-elles bien valorisées ?"
              />
            </div>
          </div>

          {/* SECTIONS THÉMATIQUES DÉTAILLÉES */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* SECTION E — PERFORMANCE FINANCIÈRE */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-400" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  Performance financière
                </h4>
              </div>
              <div className="space-y-3">
                <DetailMetricRow
                  fieldDef={fixedCostsMetric.fieldDef}
                  target={fixedCostsMetric.target}
                  actual={fixedCostsMetric.actual}
                  forecast={fixedCostsMetric.forecast}
                  delta={fixedCostsMetric.delta}
                  status={fixedCostsMetric.status}
                  closed={closed}
                  invertedPolarity
                />
                <DetailMetricRow
                  fieldDef={netResultMetric.fieldDef}
                  target={netResultMetric.target}
                  actual={netResultMetric.actual}
                  forecast={netResultMetric.forecast}
                  delta={netResultMetric.delta}
                  status={netResultMetric.status}
                  closed={closed}
                />
              </div>
            </div>

            {/* SECTION F — PRODUCTION & STAFFING */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-purple-400" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  Production &amp; Staffing
                </h4>
              </div>
              <div className="space-y-3">
                <DetailMetricRow
                  fieldDef={etpMetric.fieldDef}
                  target={etpMetric.target}
                  actual={etpMetric.actual}
                  forecast={etpMetric.forecast}
                  delta={etpMetric.delta}
                  status={etpMetric.status}
                  closed={closed}
                  customExtra={
                    etpMetric.actual != null && (
                      <span className="text-[11px] text-slate-400">
                        Capacité : {formatPilotageDecimal(etpMetric.actual * 18)} j
                      </span>
                    )
                  }
                />
                <DetailMetricRow
                  fieldDef={tjmMetric.fieldDef}
                  target={tjmMetric.target}
                  actual={tjmMetric.actual}
                  forecast={tjmMetric.forecast}
                  delta={tjmMetric.delta}
                  status={tjmMetric.status}
                  closed={closed}
                />
              </div>
            </div>

            {/* SECTION G — DÉVELOPPEMENT DES EFFECTIFS */}
            <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-amber-400" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  Développement des effectifs
                </h4>
              </div>
              <div className="space-y-3">
                <DetailMetricRow
                  fieldDef={totalHeadcountMetric.fieldDef}
                  target={totalHeadcountMetric.target}
                  actual={totalHeadcountMetric.actual}
                  forecast={totalHeadcountMetric.forecast}
                  delta={totalHeadcountMetric.delta}
                  status={totalHeadcountMetric.status}
                  closed={closed}
                />
                <DetailMetricRow
                  fieldDef={entriesMetric.fieldDef}
                  target={entriesMetric.target}
                  actual={entriesMetric.actual}
                  forecast={entriesMetric.forecast}
                  delta={entriesMetric.delta}
                  status={entriesMetric.status}
                  closed={closed}
                />
              </div>
            </div>
          </div>

          {/* SECTION H — ÉVOLUTION SUR 12 MOIS */}
          <MonthlyExecutiveCharts
            year={year}
            selectedMonth={month}
            onSelectMonth={selectMonth}
            objective={data.objective}
            cockpitData={cockpitData}
            selectedMonthData={data}
            monthsData={data.months || []}
          />
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------
// SOUS-COMPOSANT : CARTE KPI PRIORITAIRE DE DIRECTION (SECTION D)
// ----------------------------------------------------------------------
function ExecutiveKpiCard({
  fieldDef,
  target,
  actual,
  forecast,
  delta,
  status,
  closed,
  accentColor,
  icon,
  questionLabel,
  isMargin = false,
  invertedPolarity = false,
}) {
  const isTargetSet = target !== null && target !== undefined;
  const isActualSet = actual !== null && actual !== undefined;
  const isForecastSet = forecast !== null && forecast !== undefined;

  let statusBadgeStyle = "bg-slate-800/60 text-slate-400 border-slate-700/50";
  if (status === "OK") statusBadgeStyle = "bg-emerald-500/10 text-emerald-300 border-emerald-500/20";
  else if (status === "BAD") statusBadgeStyle = "bg-rose-500/10 text-rose-300 border-rose-500/20";
  else if (status === "WARN") statusBadgeStyle = "bg-amber-500/10 text-amber-200 border-amber-500/20";

  let progressPct = null;
  if (closed && isTargetSet && isActualSet && target > 0) {
    progressPct = Math.round((actual / target) * 1000) / 10;
  }

  return (
    <article
      className={`relative flex flex-col justify-between rounded-2xl border border-white/10 bg-slate-950/60 p-5 shadow-sm transition hover:border-white/20 hover:bg-slate-950/80 ${accentColor}`}
      data-testid={`monthly-metric-${fieldDef.key}`}
    >
      <div>
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
            {fieldDef.label}
          </h4>
          <span className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-slate-300">
            {icon}
          </span>
        </div>

        {questionLabel && (
          <p className="mt-1 text-[11px] font-medium text-slate-400 leading-tight">
            {questionLabel}
          </p>
        )}

        {/* VALEUR PRINCIPALE DOMINANTE : RÉALISÉ OU PRÉVISION DU MOIS */}
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              {closed ? "RÉALISÉ" : isForecastSet ? "PRÉVISION DU MOIS" : "Statut"}
            </span>
            {closed && (
              <span
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${statusBadgeStyle}`}
              >
                {!isTargetSet ? "Objectif non défini" : STATUS_LABELS[status] || status}
              </span>
            )}
            {!closed && isForecastSet && (
              <span className="inline-flex items-center gap-1 rounded-md border border-lenvia-blue/30 bg-lenvia-blue/10 px-2 py-0.5 text-[11px] font-semibold text-sky-300">
                Prévision
              </span>
            )}
          </div>
          <p className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
            {closed
              ? isActualSet
                ? formatMonthlyValue(actual, fieldDef)
                : "Réalisé non disponible"
              : isForecastSet
              ? formatMonthlyValue(forecast, fieldDef)
              : "À réaliser"}
          </p>
        </div>

        {/* VALEUR SECONDAIRE : OBJECTIF */}
        <div className="mt-3 flex items-baseline justify-between rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2 text-xs">
          <span className="text-slate-400">Objectif :</span>
          <span className={`font-semibold ${!isTargetSet ? "text-slate-400 italic" : "text-slate-200"}`}>
            {!isTargetSet ? "Objectif non défini" : formatMonthlyValue(target, fieldDef)}
          </span>
        </div>
      </div>

      <div className="mt-4 border-t border-white/10 pt-3">
        {closed ? (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-xs">
              <span className="text-slate-300">
                Réalisé :{" "}
                <span className="font-semibold text-white">
                  {formatMonthlyValue(actual, fieldDef)}
                </span>
              </span>
              <span className="text-slate-400">
                Écart :{" "}
                <span
                  className={`font-semibold ${
                    delta != null
                      ? (invertedPolarity ? delta <= 0 : delta >= 0)
                        ? "text-emerald-400"
                        : "text-rose-400"
                      : "text-slate-400"
                  }`}
                >
                  {delta != null && delta > 0 ? "+" : ""}
                  {formatMonthlyValue(delta, fieldDef)}
                </span>
              </span>
            </div>

            {progressPct != null && !isMargin && (
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-slate-400">
                  <span>Atteinte</span>
                  <span className="font-semibold text-slate-300">{progressPct.toFixed(1).replace(".", ",")} %</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                  <div
                    className={`h-full rounded-full transition-all ${
                      progressPct >= 100 ? "bg-emerald-400" : progressPct >= 90 ? "bg-amber-400" : "bg-lenvia-blue"
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1 text-xs text-slate-400">
            <p>
              {!isTargetSet
                ? "Objectif non défini · prévision issue des missions"
                : "Prévision du mois calculée selon les missions actives"}
            </p>
            {fieldDef.hint && (
              <p className="text-[11px] text-slate-400 leading-snug">
                {fieldDef.hint}
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

// ----------------------------------------------------------------------
// SOUS-COMPOSANT : LIGNE MÉTRIQUE DÉTAILLÉE (SECTIONS E, F, G)
// ----------------------------------------------------------------------
function DetailMetricRow({
  fieldDef,
  target,
  actual,
  forecast,
  delta,
  status,
  closed,
  invertedPolarity = false,
  customExtra = null,
}) {
  const isTargetSet = target !== null && target !== undefined;
  const isActualSet = actual !== null && actual !== undefined;
  const isForecastSet = forecast !== null && forecast !== undefined;

  let statusBadgeStyle = "bg-slate-800/60 text-slate-400 border-slate-700/50";
  if (status === "OK") statusBadgeStyle = "bg-emerald-500/10 text-emerald-300 border-emerald-500/20";
  else if (status === "BAD") statusBadgeStyle = "bg-rose-500/10 text-rose-300 border-rose-500/20";
  else if (status === "WARN") statusBadgeStyle = "bg-amber-500/10 text-amber-200 border-amber-500/20";

  return (
    <article
      className="rounded-xl border border-white/5 bg-slate-900/60 p-3.5 transition hover:bg-slate-900/90"
      data-testid={`monthly-metric-${fieldDef.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h4 className="text-xs font-semibold text-slate-200">
          {fieldDef.label}
        </h4>
        {closed && isTargetSet && isActualSet && (
          <span
            className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold ${statusBadgeStyle}`}
          >
            {STATUS_LABELS[status] || status}
          </span>
        )}
      </div>

      {/* VALEUR PRINCIPALE : RÉALISÉ OU PRÉVISION */}
      <div className="mt-2.5">
        <span className="block text-[10px] uppercase tracking-wider text-slate-400">
          {closed ? "RÉALISÉ" : isForecastSet ? "PRÉVISION DU MOIS" : "Statut"}
        </span>
        <span className="text-xl font-extrabold text-white">
          {closed
            ? isActualSet
              ? formatMonthlyValue(actual, fieldDef)
              : "Réalisé non disponible"
            : isForecastSet
            ? formatMonthlyValue(forecast, fieldDef)
            : "À réaliser"}
        </span>
      </div>

      {/* VALEUR SECONDAIRE : OBJECTIF */}
      <div className="mt-2 flex items-baseline justify-between rounded bg-white/[0.03] border border-white/5 px-2.5 py-1.5 text-xs">
        <span className="text-slate-400">Objectif :</span>
        <span className={`font-semibold ${!isTargetSet ? "text-slate-400 italic" : "text-slate-200"}`}>
          {!isTargetSet ? "Objectif non défini" : formatMonthlyValue(target, fieldDef)}
        </span>
      </div>

      <div className="mt-2.5 border-t border-white/10 pt-2 text-xs">
        {closed ? (
          <div className="flex flex-wrap items-center justify-between gap-1">
            <span className="text-slate-300">
              Réalisé : <span className="font-semibold text-white">{formatMonthlyValue(actual, fieldDef)}</span>
            </span>
            <span className="text-slate-400">
              Écart :{" "}
              <span
                className={`font-semibold ${
                  delta != null
                    ? (invertedPolarity ? delta <= 0 : delta >= 0)
                      ? "text-emerald-400"
                      : "text-rose-400"
                    : "text-slate-400"
                }`}
              >
                {delta != null && delta > 0 ? "+" : ""}
                {formatMonthlyValue(delta, fieldDef)}
              </span>
            </span>
          </div>
        ) : (
          <p className="text-[11px] text-slate-400">
            {!isTargetSet
              ? "Objectif non défini · prévision issue des missions"
              : "Prévision du mois calculée selon les missions actives"}
          </p>
        )}
      </div>
    </article>
  );
}
